import * as THREE from 'three';
import { Renderer } from './renderer/Renderer';
import { LiquidBackground } from './liquid/LiquidBackground';

/**
 * Application entry. Single fullscreen liquid-metal shader pass; DOM text
 * sits over it via the overlay.
 */
export class App {
  private readonly renderer: Renderer;
  private readonly bg: LiquidBackground;
  private readonly clock = new THREE.Clock();
  private readonly mouseUv = new THREE.Vector2(0.5, 0.5);
  private readonly mouseTarget = new THREE.Vector2(0.5, 0.5);
  private readonly reducedMotion: boolean;
  private readonly frozenTime: number | null;
  private rafId = 0;
  private elapsed = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer({ canvas });
    this.bg = new LiquidBackground();

    const { width, height } = this.renderer.size();
    this.bg.setSize(width, height);

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.frozenTime = this.reducedMotion ? 18.0 : null;

    window.addEventListener('resize', this.onResize);
    window.addEventListener('pointermove', this.onPointerMove);
  }

  start(): void {
    this.tick();
  }

  stop(): void {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointermove', this.onPointerMove);
    this.bg.dispose();
    this.renderer.dispose();
  }

  private onResize = (): void => {
    const { width, height } = this.renderer.size();
    this.bg.setSize(width, height);
  };

  private onPointerMove = (e: PointerEvent): void => {
    this.mouseTarget.set(e.clientX / window.innerWidth, 1 - e.clientY / window.innerHeight);
  };

  private tick = (): void => {
    this.rafId = requestAnimationFrame(this.tick);
    if (this.renderer.isPaused()) return;

    const dt = Math.min(this.clock.getDelta(), 1 / 30);
    if (this.frozenTime === null) {
      this.elapsed += dt;
    } else {
      this.elapsed = this.frozenTime;
    }

    // Smooth cursor — the shader picks up cursor as a soft drift, not a hard cursor.
    this.mouseUv.lerp(this.mouseTarget, 0.06);
    this.bg.setMouse(this.mouseUv);
    this.bg.setTime(this.elapsed);

    this.renderer.gl.render(this.bg.scene, this.bg.camera);
  };
}
